import { AgsExitFreespinsState } from "ags/state-machine/states/ags-exit-freespins-state";
import { gameState } from "appworks/model/game-state";
import { Contract } from "appworks/utils/contracts/contract";
import { Components } from "appworks/components/components";
import { MatrixComponent } from "slotworks/components/matrix/matrix-component";
import { SpinRecord } from "slotworks/model/gameplay/records/spin-record";
import { Parallel } from "appworks/utils/contracts/parallel";
import { slotDefinition } from "slotworks/model/slot-definition";
import { staggerSceneChanges } from "util/stagger-scene-changes";
import { Services } from "appworks/services/services";
import { SlotBetService } from "slotworks/services/bet/slot-bet-service";
import { BigWinThresholds, winMagnitude } from "ags/utils/win-magnitude";
import { BigWinComponent } from "ags/components/big-win-component";
import { TransactionService } from "appworks/services/transaction/transaction-service";
import { FreespinRecord } from "slotworks/model/gameplay/records/freespin-record";
import { SoundService } from "appworks/services/sound/sound-service";
import { Sequence } from "appworks/utils/contracts/sequence";
import { UIFlag, uiFlags } from "appworks/ui/flags/ui-flags";
import { FooterComponent } from "slotworks/components/footer/footer-component";
import { bigWinCommand } from "ags/commands/bigwin-command";

export class ThemeExitFreespinsState extends AgsExitFreespinsState {
    protected cashWon: number;
    protected winRatio: number;
    protected bigWinLevel: BigWinThresholds;
    protected soundService: SoundService;
    public onEnter(): void {
        const record = gameState.getCurrentGame().getCurrentRecord();
        this.soundService = Services.get(SoundService);

        if (!record.sumWins) {
            gameWrapper.balancesUpdate(
                gameState.getCurrentGame().realBalance,
                gameState.getCurrentGame().freeBalance
            );
        }
        //setting up biglevel for animation
        this.cashWon = gameState.getCurrentGame().getTotalWin();
        this.winRatio = this.cashWon / Services.get(SlotBetService).getTotalStake();
        this.bigWinLevel = winMagnitude.getBigWinLevel(this.winRatio);

        // Always clear the footer before a big win or summary prompt
        Components.get(FooterComponent).showFreespinStart();

        if (this.bigWinLevel >= BigWinThresholds.BIG) {
            bigWinCommand.start(this.bigWinLevel);
            this.soundPlayAndSetWinings().execute();
        } else {
            super.onEnter();
        }
    }

    public onExit(): void {
        Components.get(FooterComponent).clear();
        if (this.bigWinLevel >= BigWinThresholds.BIG) {
            bigWinCommand.stop(true).execute();
        }
        super.onExit();
    }

    protected isFreespinRecord() {
        const currentGame = gameState.getCurrentGame();
        return currentGame.isCurrentRecordType(FreespinRecord);
    }

    protected resetMatrix(): Contract<void> {
        // Handled in hideFreespinsComponents during screen wipe
        return Contract.empty();
    }

    protected hideFreespinsComponents() {
        const matrix = Components.get(MatrixComponent);
        const rootRecord = gameState.getCurrentGame().getRootRecord() as SpinRecord;
        return new Parallel<void>([
            () => Contract.getDelayedContract(500, () => new Parallel([
                () => super.hideFreespinsComponents(),
                () => Contract.wrap(() => {
                    matrix.change("default", slotDefinition.matrixGrid);
                    matrix.jumpToGrid(rootRecord.grid);
                    staggerSceneChanges(["MatrixForeground", "MatrixBackground", "SymbolAnimations", "Jackpots", "Banners"], "default");
                })
            ]))
        ]);
    }

    protected resetToTriggeringSpin(): Contract<void> {
        gameState.getCurrentGame().resetToRootRecord();
        return super.resetToTriggeringSpin();
    }

    protected soundPlayAndSetWinings(): Contract<void> {
        const winRatio = this.cashWon / Services.get(SlotBetService).getTotalStake();
        const isBigWin = winRatio >= BigWinThresholds.BIG;
        const shouldPlayBonusSound = this.isFreespinRecord() && !isBigWin;
        const duration = winMagnitude.getSoundDuration(winRatio, shouldPlayBonusSound);
        const soundComplete = this.getAudioContract(winRatio, shouldPlayBonusSound, isBigWin);

        const methods: Array<() => Contract<void>> = [
            () => new Parallel<any>([
                () => this.setWinningsForTickUp(duration),
                () => soundComplete()
            ])
        ];

        methods.push(() => Contract.wrap(() => this.soundService.customEvent("end_rollup")));
        return new Sequence<any>(methods);
    }

    protected setWinningsForTickUp(duration: number): Contract<void> {
        return new Contract((resolve) => {
            Services.get(TransactionService).setTotalWin(0)
            Services.get(TransactionService).setWinnings(this.cashWon, this.cashWon, duration).then(() => {
                this.cleanUpLayersAndReels().execute();
                this.complete();
            });
        });
    }

    protected getAudioContract(winRatio: number, shouldPlayBonusSound: boolean, isHugeWin: boolean): () => Contract<void> {
        if (shouldPlayBonusSound) {
            this.soundService.customEvent("ringup1");
            return Contract.empty;
        }

        if (winRatio === 0) {
            return Contract.empty;
        }

        const bigWinSound = winMagnitude.getSoundAlias(winRatio);

        if (!isHugeWin) {
            this.soundService.customEvent(bigWinSound);
            return Contract.empty;
        }
        return Contract.empty;
    }

    public onSkip() {
        if (this.bigWinLevel >= BigWinThresholds.BIG) {
            this.cleanUpLayersAndReels().execute();
            this.skip();
        }
    }

    private cleanUpLayersAndReels() {
        return new Parallel([
            () => this.playEndRollupSound(),
            () => this.resetReels(),
            () => this.updateSceneAndUIFlags(),
        ]);
    }

    private playEndRollupSound(): Contract<void> {
        this.soundService.customEvent("end_rollup");
        this.soundService.customEvent("exit_scene_Prompts_freespinssummary");
        return Contract.empty();
    }

    private resetReels(): Contract<void> {
        const matrixComponent = Components.get(MatrixComponent);
        const rootRecord = gameState.getCurrentGame().getRootRecord() as SpinRecord;
        matrixComponent.change("default", slotDefinition.matrixGrid);
        matrixComponent.jumpToGrid(rootRecord.grid);
        return Contract.empty();
    }

    private updateSceneAndUIFlags(): Contract<void> {
        staggerSceneChanges(["Background", "MatrixForeground", "MatrixBackground", "SymbolAnimations", "Jackpots", "Banners"], "default");
        uiFlags.set(UIFlag.FREE_SPINS, false);
        uiFlags.set(UIFlag.PROMPT, false);
        return Contract.empty();
    }
}
